/**
 * API response shapes.
 *
 * These mirror the backend's Pydantic schemas by hand for now. From Phase 0
 * completion they are **generated** from the OpenAPI document
 * (`openapi-typescript` + `orval`) and committed — a backend field rename then
 * becomes a frontend compile error in the same pull request, which is the whole
 * point. Hand-written client types are how frontend and backend drift.
 * docs/07-frontend.md §4
 */

import type { Permission, Scope } from "@/shared/auth/permissions";

export interface TenantSummary {
  id: string;
  slug: string;
  name: string;
  status: "provisioning" | "trial" | "active" | "past_due" | "suspended" | "archived" | "failed";
  timezone: string;
  locale: string;
  currency: string;
}

export interface UserSummary {
  id: string;
  full_name: string;
  email: string | null;
  phone: string | null;
  locale: string;
}

export type PortalId = "admin" | "teacher" | "parent";

export interface SessionSnapshot {
  user: UserSummary;
  tenant: TenantSummary;
  membership_id: string;
  roles: string[];
  permissions: Permission[];
  scopes: Partial<Record<Permission, Scope>>;
  portal: PortalId;
  session_id: string;
  authenticated_at: string;
  /**
   * Which person this login stands for, when it stands for one.
   *
   * The family portal needs it and cannot get it any other way: a student holds
   * `student.view.self`, not `student.view`, so asking the students endpoint
   * "who am I" returns 403.
   */
  student_id: string | null;
  guardian_id: string | null;
}

export interface TokenResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  session: SessionSnapshot;
}

export interface MembershipOption {
  membership_id: string;
  tenant: TenantSummary;
  roles: string[];
  display_name: string | null;
}

export interface TenantSelectionRequired {
  requires_tenant_selection: true;
  login_token: string;
  expires_in: number;
  memberships: MembershipOption[];
}

/**
 * Issued when a platform super admin signs in.
 *
 * Deliberately shaped unlike `TokenResponse`: no tenant, no membership, no
 * school permissions, because platform staff are a separate authorization
 * realm rather than a very powerful school role. There is no `session`, and a
 * platform token satisfies no tenant-plane route.
 * docs/04-auth-authz.md §5.2
 */
export interface PlatformSessionResponse {
  access_token: string;
  token_type: "Bearer";
  expires_in: number;
  realm: "platform";
  user: UserSummary;
}

export type LoginResult =
  | TokenResponse
  | TenantSelectionRequired
  | PlatformSessionResponse;

export function needsTenantSelection(result: LoginResult): result is TenantSelectionRequired {
  return "requires_tenant_selection" in result;
}

/**
 * A platform sign-in, which this application cannot carry.
 *
 * The three portals here are tenant-scoped; the super-admin console is a
 * separate entry point. Without this guard `result.session` is `undefined` and
 * every caller reading `.portal` off it throws — which is not an `ApiError`,
 * so the login page reports "something went wrong" for a request the server
 * answered with 200.
 */
export function isPlatformSession(result: LoginResult): result is PlatformSessionResponse {
  return "realm" in result && result.realm === "platform";
}

/** Shown by both sign-in entrances, so they cannot drift apart. */
export const PLATFORM_SIGN_IN_MESSAGE =
  "This is a platform staff account. It is not linked to a school, so it " +
  "cannot be used here — sign in with a school account instead.";

export interface ServiceInfo {
  name: string;
  version: string;
  environment: string;
  api_version: string;
  docs_url: string | null;
  server_time: string;
  timezone_note: string;
}

export interface DependencyStatus {
  name: string;
  healthy: boolean;
  detail: string | null;
}

export interface ReadinessResponse {
  status: "ready" | "degraded";
  version: string;
  dependencies: DependencyStatus[];
}

export interface SchoolProfile {
  display_name: string;
  motto: string | null;
  board_affiliation: string | null;
  affiliation_number: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  website: string | null;
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  postal_code: string | null;
  country: string;
  academic_year_start_month: number;
  attendance_mode: "daily" | "period";
  week_start: "monday" | "sunday";
  logo_url: string | null;
  primary_color: string | null;
  preferences: Record<string, unknown>;
}

export interface School extends TenantSummary {
  legal_name: string | null;
  profile: SchoolProfile;
}

export interface SchoolOverview {
  school: School;
  your_roles: string[];
  your_permission_count: number;
}

export interface RoleSummary {
  id: string;
  code: string;
  name: string;
  description: string | null;
  is_system: boolean;
}

export interface RoleDetail extends RoleSummary {
  /** permission code → scope. */
  permissions: Partial<Record<Permission, Scope>>;
}

export interface SchoolUser {
  membership_id: string;
  user_id: string;
  full_name: string;
  display_name: string | null;
  email: string | null;
  phone: string | null;
  status: "invited" | "active" | "inactive";
  user_status: string;
  roles: RoleSummary[];
  created_at: string;
  deactivated_at: string | null;
}

export interface Page<T> {
  data: T[];
  total: number | null;
  limit: number;
  offset: number;
}

// --- federated sign-in -------------------------------------------------------

export interface ProviderOption {
  slug: string;
  display_name: string;
  kind:
    | "generic_oidc"
    | "auth0"
    | "keycloak"
    | "entra_id"
    | "google_workspace"
    | "okta";
}

export interface AuthorizationResponse {
  authorization_url: string;
  state: string;
  provider_name: string;
}

export interface CallbackResponse {
  result: LoginResult;
  return_to: string | null;
}
